import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface SystemLog {
    _id: string;
    userId?: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        refNo: string;
    };
    subscriptionId?: {
        _id: string;
        companyName: string;
    };
    action: string;
    type: string;
    status: string;
    ipAddress: string;
    description: string;
    timestamp: Date;
    userAgent?: string;
    method?: string;
    statusCode?: number;
    requestBody?: any;
    responseBody?: any;
    errorMessage?: string;
    duration?: number;
}

export interface PagedLogsRequest {
    pageIndex: number;
    pageSize: number;
    sortField: string;
    sortOrder: 1 | -1;
    filters: {
        subscriptionId?: string;
        userId?: string;
        action?: string;
        type?: string;
        status?: string;
        ipAddress?: string;
        startDate?: Date;
        endDate?: Date;
        searchText?: string;
    };
    selectedSubscriberId?: string;
}

export interface PagedLogsResponse {
    message: string;
    data: SystemLog[];
    metadata: Array<{
        total: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
    }>;
}

export interface LogStats {
    totalLogs: number;
    successCount: number;
    failureCount: number;
    uniqueUsers: number;
    uniqueIPs: number;
    topActions: Array<{ action: string; count: number }>;
    topTypes: Array<{ type: string; count: number }>;
}

export interface SystemHealthMetrics {
    totalLogs: number;
    todayLogs: number;
    errorRate: number;
    avgResponseTime: number;
    topErrors: Array<{ errorMessage: string; count: number }>;
}

@Injectable({
    providedIn: 'root'
})
export class SystemLogService {
    private apiUrl = `${environment.apiUrl}/system-logs`;

    constructor(private http: HttpClient) {}

    /**
     * Get paged logs by subscription with advanced filtering
     */
    getPagedLogs(request: PagedLogsRequest): Observable<PagedLogsResponse> {
        return this.http.post<PagedLogsResponse>(`${this.apiUrl}/paged`, request);
    }

    /**
     * Get logs with basic filtering
     */
    getLogs(params: any = {}): Observable<any> {
        return this.http.get(`${this.apiUrl}`, { params });
    }

    /**
     * Get log statistics
     */
    getLogStats(params: any = {}): Observable<{ success: boolean; data: LogStats }> {
        return this.http.get<{ success: boolean; data: LogStats }>(`${this.apiUrl}/stats`, { params });
    }

    /**
     * Get system health metrics
     */
    getSystemHealthMetrics(): Observable<{ success: boolean; data: SystemHealthMetrics }> {
        return this.http.get<{ success: boolean; data: SystemHealthMetrics }>(`${this.apiUrl}/health`);
    }

    /**
     * Get logs for a specific user
     */
    getUserLogs(userId: string): Observable<any> {
        return this.http.get(`${this.apiUrl}/user/${userId}`);
    }

    /**
     * Get logs for a specific IP address
     */
    getIPLogs(ipAddress: string): Observable<any> {
        return this.http.get(`${this.apiUrl}/ip/${ipAddress}`);
    }

    /**
     * Get failed login attempts
     */
    getFailedLoginAttempts(): Observable<any> {
        return this.http.get(`${this.apiUrl}/failed-logins`);
    }

    /**
     * Get a specific log by ID
     */
    getLogById(logId: string): Observable<any> {
        return this.http.get(`${this.apiUrl}/${logId}`);
    }

    /**
     * Export logs
     */
    exportLogs(params: any = {}): Observable<any> {
        return this.http.get(`${this.apiUrl}/export`, { params });
    }

    /**
     * Clean old logs
     */
    cleanOldLogs(): Observable<any> {
        return this.http.delete(`${this.apiUrl}/clean`);
    }
}
